import { Status, type StatusCode, type StatusCounts, type TimelineSlot } from '@status/shared';

export interface StatusVisual {
  label: string;
  /** 时间轴格子 / 圆点的背景色 */
  swatch: string;
  /** 徽章的完整配色 */
  badge: string;
  dot: string;
  /** 纯文字配色。和 badge 用同一份状态定义，避免两处颜色各自漂移。 */
  text: string;
}

const VISUALS: Record<StatusCode, StatusVisual> = {
  [Status.AVAILABLE]: {
    label: '正常',
    swatch: 'bg-emerald-500',
    badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  [Status.DEGRADED]: {
    label: '降级',
    swatch: 'bg-amber-500',
    badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-500/30',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
  },
  [Status.UNAVAILABLE]: {
    label: '故障',
    swatch: 'bg-rose-500',
    badge: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-500/30',
    dot: 'bg-rose-500',
    text: 'text-rose-600 dark:text-rose-400',
  },
};

const NO_DATA: StatusVisual = {
  label: '暂无数据',
  swatch: 'bg-slate-300 dark:bg-slate-700',
  badge: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 ring-slate-500/30',
  dot: 'bg-slate-400',
  text: 'text-slate-500 dark:text-slate-400',
};

export function statusVisual(status: StatusCode, hasData = true): StatusVisual {
  if (!hasData) return NO_DATA;
  return VISUALS[status] ?? NO_DATA;
}

/** 时间轴某一格是否有真实探测数据 */
export function slotHasData(slot: TimelineSlot): boolean {
  return slot.status_counts.missing === 0;
}

export function formatLatency(ms: number): string {
  if (ms <= 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * 可用率。hasData 必须显式传入：全程故障的模型可用率是 0%，
 * 和「没有探测数据」是两件事，不能都显示成 —。
 */
export function formatUptime(pct: number, hasData: boolean): string {
  if (!hasData) return '—';
  // 99.95 这种数字四舍五入到 100% 会掩盖故障，所以不足 100 时保底显示 99.9
  if (pct >= 99.95 && pct < 100) return '99.9%';
  return `${pct.toFixed(pct >= 99 ? 2 : 1)}%`;
}

export function formatRelativeTime(tsSeconds: number, now = Date.now()): string {
  if (tsSeconds <= 0) return '从未探测';
  const diff = Math.floor(now / 1000) - tsSeconds;
  if (diff < 0) return '刚刚';
  if (diff < 60) return `${diff} 秒前`;
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)} 小时前`;
  return `${Math.floor(diff / 86_400)} 天前`;
}

/**
 * 显示到秒。探测按 5 分钟对齐发起，「下次探测 13:55:00」的秒位能直接
 * 印证节奏是否还在对齐点上；只显示到分钟则看不出偏移。
 * hourCycle 固定 h23，避免某些环境把 00 点显示成 24 点。
 */
export function formatClock(tsSeconds: number): string {
  if (tsSeconds <= 0) return '—';
  return new Date(tsSeconds * 1000).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
}

/** 把失败分类翻译成中文，用于 tooltip */
const FAILURE_LABELS: Record<string, string> = {
  slow_latency: '响应慢',
  rate_limit: '限流',
  server_error: '上游错误',
  client_error: '请求错误',
  auth_error: '鉴权失败',
  invalid_request: '请求无效',
  network_error: '网络错误',
  response_timeout: '超时',
  content_mismatch: '无有效内容',
};

/** 从时间桶的失败分类中汇总实际收到的 HTTP 状态码。 */
function formatHttpCodes(counts: StatusCounts): string[] {
  const codes = new Map<string, number>();
  for (const byCode of Object.values(counts.http_code_breakdown)) {
    for (const [code, count] of Object.entries(byCode)) {
      codes.set(code, (codes.get(code) ?? 0) + count);
    }
  }
  return [...codes.entries()].map(([code, count]) => `HTTP ${code} ${count} 次`);
}

/** 生成时间轴格子的 tooltip 文案 */
export function slotTooltip(slot: TimelineSlot): string {
  if (!slotHasData(slot)) return `${slot.time} 无探测数据`;

  const c = slot.status_counts;
  const parts = [`${slot.time}`, `可用率 ${slot.availability.toFixed(1)}%`];
  if (slot.latency > 0) parts.push(`延迟 ${formatLatency(slot.latency)}`);

  const total = c.available + c.degraded + c.unavailable;
  parts.push(`探测 ${total} 次`);

  const reasons = Object.entries(FAILURE_LABELS)
    .filter(([key]) => (c[key as keyof typeof c] as number) > 0)
    .map(([key, label]) => `${label} ${c[key as keyof typeof c] as number}`);
  if (reasons.length > 0) parts.push(reasons.join(' / '));
  const httpCodes = formatHttpCodes(c);
  if (httpCodes.length > 0) parts.push(httpCodes.join(' / '));

  return parts.join(' · ');
}
